// Hierarchical USD budgets. All wire values are USD micros (1 USD = 1_000_000);
// 0 means "no cap / inherit from parent" — never "free".

export type BudgetScope = 'org' | 'workspace' | 'repo' | 'chat';

export interface OrgBudget {
  id: string;
  org_id: string;
  daily_limit: number; // legacy token caps — NOT enforced; counters below are telemetry only
  monthly_limit: number;
  tokens_today: number;
  tokens_month: number;
  daily_cost_limit_usd_micros: number;
  monthly_cost_limit_usd_micros: number;
  cost_today_usd_micros: number;
  cost_month_usd_micros: number;
  chat_session_cost_limit_usd_micros: number;
  reset_day_at: string;
  reset_month_at: string;
  updated_at: string;
  /** M3 Stage 3: action categories allowed at this scope. `read` is
   * implicit and never appears here. Lower scopes (workspace, repo,
   * schedule) intersect with this set. */
  granted_categories: string[];
  /** M3 Stage 4: inheritable schedule auto-disable threshold. Null
   * means "inherit from parent"; engine default is 5 when every
   * level in the chain is null. */
  max_consecutive_failures?: number;
}

export interface ScopedBudget {
  id: string;
  org_id: string;
  workspace_id?: string;
  repo_id?: string;
  daily_cost_limit_usd_micros: number;
  monthly_cost_limit_usd_micros: number;
  chat_session_cost_limit_usd_micros: number;
  cost_today_usd_micros: number;
  cost_month_usd_micros: number;
  reset_day_at: string;
  reset_month_at: string;
  updated_at: string;
  /** M3 Stage 3: see OrgBudget.granted_categories. Subset of parent. */
  granted_categories: string[];
  /** M3 Stage 4: see OrgBudget.max_consecutive_failures. */
  max_consecutive_failures?: number;
}

export interface ChatBudget {
  id: string;
  org_id: string;
  workspace_id: string;
  repo_id: string;
  chat_session_id: string;
  cost_limit_usd_micros: number;
  cost_total_usd_micros: number;
  created_by_user_id: string;
  updated_at: string;
}

export interface EffectiveChatBudget {
  limit_usd_micros: number;
  source: BudgetScope;
  spent_usd_micros: number;
}

export interface UpdateOrgBudgetRequest {
  daily_cost_limit_usd_micros?: number;
  monthly_cost_limit_usd_micros?: number;
  chat_session_cost_limit_usd_micros?: number;
  /** M3 Stage 3: replace the action-category grant set. Omit to leave
   * unchanged; empty array relaxes to read-only. */
  granted_categories?: string[];
  /** M3 Stage 4: per-scope schedule auto-disable cap. Omit to leave
   * unchanged. */
  max_consecutive_failures?: number;
}

export interface UpdateScopedBudgetRequest {
  daily_cost_limit_usd_micros?: number;
  monthly_cost_limit_usd_micros?: number;
  chat_session_cost_limit_usd_micros?: number;
  /** M3 Stage 3: replace the action-category grant set. Server rejects
   * (422 grants_outside_parent) when the requested set isn't a subset
   * of the parent scope's effective grant. */
  granted_categories?: string[];
  /** M3 Stage 4: per-scope schedule auto-disable cap. */
  max_consecutive_failures?: number;
}

export interface UpdateChatBudgetRequest {
  cost_limit_usd_micros: number;
}

export interface UpdateCostLimitRequest {
  cost_limit_usd_micros: number;
}

export const usdMicros = {
  toDollars: (micros: number): number => micros / 1_000_000,
  fromDollars: (dollars: number): number => Math.round(dollars * 1_000_000),
};
